import type { MailAddress } from '@shared/mail';
import { formatDayTime } from '@/lib/format';

// Small helpers shared by the Inbox panes.

export function firstWord(name: string | null | undefined): string {
  const clean = (name ?? '').replace(/^(Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, '').trim();
  return clean.split(/\s+/)[0] || clean;
}

/** "Dr. Lena Hoffmann" reads "Lena Hoffmann" in tight rows. */
export function plainName(name: string): string {
  return name.replace(/^(Dr|Prof)\.?\s+/i, '');
}

export function addressList(list: MailAddress[]): string {
  return list.map((a) => a.name || a.email).join(', ');
}

/** A local time on a day offset from today. */
function at(daysAhead: number, hour: number, minute = 0): Date {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(hour, minute, 0, 0);
  return d;
}

export interface TimePreset {
  id: string;
  label: string;
  when: Date;
}

/** Later today (before 15:00), tomorrow morning, and the next Monday morning. */
export function timePresets(): TimePreset[] {
  const now = new Date();
  const out: TimePreset[] = [];
  if (now.getHours() < 15) out.push({ id: 'later', label: 'Later today', when: at(0, 16) });
  out.push({ id: 'tomorrow', label: 'Tomorrow morning', when: at(1, 9) });
  const toMonday = ((8 - now.getDay()) % 7) || 7;
  if (toMonday > 1) out.push({ id: 'monday', label: 'Monday morning', when: at(toMonday, 9) });
  else out.push({ id: 'next-week', label: 'Next week', when: at(7, 9) });
  return out;
}

export function presetSub(p: TimePreset): string {
  return formatDayTime(p.when);
}

/** The browser's time zone name, shown next to scheduled times ("London"). */
export function zoneCity(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  return (zone.split('/').pop() ?? '').replace(/_/g, ' ');
}
